import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { PersonnelRateSkillsDialog } from '../components/personnel/PersonnelRateSkillsDialog'
import { useAdminCompany } from '../hooks/useAdminCompany'
import type { CompanyMemberDocument } from '@/types'

export function PersonnelPage() {
  const { members, loading, error, updateMember } = useAdminCompany()
  const [search, setSearch] = useState('')
  const [editTarget, setEditTarget] = useState<(CompanyMemberDocument & { id: string }) | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  function openEdit(member: CompanyMemberDocument & { id: string }) {
    setEditTarget(member)
    setDialogOpen(true)
  }

  async function handleSaveRateSkills(id: string, hourlyRate: number | undefined, productionSkills: string[]) {
    await updateMember(id, { hourlyRate, productionSkills })
  }

  const filtered = members.filter((m) => {
    if (!m.isActive) return false
    if (!m.availableForHire) return false
    if (!search) return true
    const term = search.toLowerCase()
    return (
      m.name.toLowerCase().includes(term) ||
      (m.productionSkills ?? []).some((s) => s.toLowerCase().includes(term)) ||
      m.roles.some((r) => r.toLowerCase().includes(term))
    )
  })

  return (
    <AdminListPage
      title="Personnel"
      action={
        <Button asChild variant="outline">
          <Link to="/admin/config/company">
            <ExternalLink className="h-4 w-4 mr-2" />
            Manage in Company Settings
          </Link>
        </Button>
      }
      filters={
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Personnel are your company members. Edit full profiles under{' '}
            <Link to="/admin/config/company" className="underline hover:text-foreground">
              Config → Company
            </Link>
            .
          </p>
          <Input
            placeholder="Search by name or skill…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        </div>
      }
      error={error}
      loading={loading}
      dialog={
        editTarget && (
          <PersonnelRateSkillsDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            member={editTarget}
            onSave={handleSaveRateSkills}
          />
        )
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Roles</TableHead>
            <TableHead>Production Skills</TableHead>
            <TableHead>Rate ($/hr)</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <MultiButtonGroup>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No personnel found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage
                          src={(member.squarePhotoUrl ?? member.photoUrl) || undefined}
                          alt={member.name}
                        />
                        <AvatarFallback className="text-xs">
                          {member.name[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{member.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {member.roles.map((role) => (
                        <Badge key={role} variant="secondary" className="text-xs">{role}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(member.productionSkills ?? []).length > 0
                        ? member.productionSkills!.map((skill) => (
                            <Badge key={skill} variant="outline" className="text-xs">{skill}</Badge>
                          ))
                        : <span className="text-muted-foreground text-sm">—</span>
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    {member.hourlyRate != null ? `$${member.hourlyRate}/hr` : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={member.isActive ? 'default' : 'secondary'}>
                      {member.isActive ? 'Active' : 'Alumni'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <MultiButton
                        variant="outline"
                        size="sm"
                        items={[
                          {
                            id: 'edit-rate',
                            icon: Pencil,
                            label: 'Edit Rate & Skills',
                            onClick: () => openEdit(member),
                          },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </MultiButtonGroup>
      </Table>
    </AdminListPage>
  )
}
